import { UtensilsCrossed, Package, Tag, Users, FileText, UserCheck, LayoutTemplate } from 'lucide-react'

// ─── FullPageLoader — generic blocking overlay ─────────────────────────────

export const FullPageLoader = ({
  title,
  subtitle,
}: {
  title?: string
  subtitle?: string
}) => {
  return (
    <div role="status" aria-live="polite" className="fixed inset-0 bg-[var(--overlay-backdrop)] backdrop-blur-[2px] flex items-center justify-center z-50">
      <div className="bg-[var(--surface-card)] rounded-2xl px-8 py-7 flex flex-col items-center shadow-[var(--shadow-elevated)] border border-[var(--border-default)] w-[min(360px,calc(100vw-2rem))]">
        <div className="animate-spin rounded-full h-9 w-9 border-[3px] border-[var(--border-default)] border-t-[var(--brand-primary)] mb-5" aria-hidden="true"></div>
        {!title && <span className="sr-only">Loading…</span>}
        {title && (
          <p className="text-base font-semibold text-[var(--text-primary)] text-center">
            {title}
          </p>
        )}
        {subtitle && (
          <p className="text-xs text-[var(--text-secondary)] mt-2 text-center">
            {subtitle}
          </p>
        )}
      </div>
    </div>
  )
}

// ─── PDFGenerationLoader — PDF-specific overlay ────────────────────────────

export type PDFType =
  | 'manager'
  | 'customer'
  | 'materials'
  | 'labels'
  | 'menu-vendor'
  | 'material-vendor'
  | 'menu-template'

type PDFConfig = {
  label: string
  hint: string
  icon: React.ComponentType<{ className?: string }>
  iconBg: string
  iconColor: string
}

const PDF_CONFIG: Record<PDFType, PDFConfig> = {
  'manager': {
    label: 'Generating Manager PDF',
    hint: 'Laying out dishes and categories per event',
    icon: UtensilsCrossed,
    iconBg: 'bg-[var(--brand-primary-subtle)]',
    iconColor: 'text-[var(--brand-primary)]',
  },
  'customer': {
    label: 'Generating Customer PDF',
    hint: 'Preparing event menu details for the customer',
    icon: UserCheck,
    iconBg: 'bg-[var(--status-info-bg)]',
    iconColor: 'text-[var(--status-info-text)]',
  },
  'materials': {
    label: 'Generating Materials PDF',
    hint: 'Listing all required materials and quantities per event',
    icon: Package,
    iconBg: 'bg-[var(--status-warning-bg)]',
    iconColor: 'text-[var(--status-warning-text)]',
  },
  'labels': {
    label: 'Generating Labels PDF',
    hint: 'Preparing label sheets for each menu item',
    icon: Tag,
    iconBg: 'bg-[var(--status-success-bg)]',
    iconColor: 'text-[var(--status-success-text)]',
  },
  'menu-vendor': {
    label: 'Generating menu item vendor report',
    hint: 'Compiling menu items by vendor across events',
    icon: Users,
    iconBg: 'bg-[var(--status-info-bg)]',
    iconColor: 'text-[var(--status-info-text)]',
  },
  'material-vendor': {
    label: 'Generating material vendor report',
    hint: 'Grouping materials and quantities per vendor',
    icon: Users,
    iconBg: 'bg-[var(--status-info-bg)]',
    iconColor: 'text-[var(--status-info-text)]',
  },
  'menu-template': {
    label: 'Generating Template PDF',
    hint: 'Laying out categories, items and OR-groups for this template',
    icon: LayoutTemplate,
    iconBg: 'bg-[var(--brand-primary-subtle)]',
    iconColor: 'text-[var(--brand-primary)]',
  },
}

const PDF_CONFIG_DEFAULT: PDFConfig = {
  label: 'Generating PDF',
  hint: 'Preparing your document, this may take a moment',
  icon: FileText,
  iconBg: 'bg-[var(--surface-hover)]',
  iconColor: 'text-[var(--text-muted)]',
}

export const PDFGenerationLoader = ({
  pdfType,
  name,
  subtitle,
}: {
  pdfType?: PDFType
  /** Event name or count string, shown prominently below the label */
  name?: string
  /** Secondary line — e.g. "Customer · ORD-2026-0001" */
  subtitle?: string
}) => {
  const config = pdfType ? PDF_CONFIG[pdfType] : PDF_CONFIG_DEFAULT
  const Icon = config.icon

  return (
    <div role="status" aria-live="polite" className="fixed inset-0 bg-[var(--overlay-backdrop)] backdrop-blur-[2px] flex items-center justify-center z-50">
      <div className="bg-[var(--surface-card)] rounded-2xl px-8 py-7 flex flex-col items-center shadow-[var(--shadow-elevated)] border border-[var(--border-default)] w-[min(360px,calc(100vw-2rem))]">

        {/* Icon badge with spinner in corner */}
        <div className="relative mb-5">
          <div className={`w-14 h-14 rounded-2xl ${config.iconBg} flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${config.iconColor}`} />
          </div>
          <div className="absolute -bottom-1.5 -right-1.5 animate-spin rounded-full h-5 w-5 border-2 border-[var(--surface-card)] border-t-[var(--brand-primary)] bg-[var(--surface-card)]"></div>
        </div>

        <p className="text-[11px] font-semibold uppercase tracking-widest text-[var(--text-muted)] mb-1.5 text-center">
          {config.label}
        </p>

        {name && (
          <p className="text-base font-semibold text-[var(--text-primary)] text-center">
            {name}
          </p>
        )}

        {subtitle && (
          <p className="text-xs text-[var(--text-secondary)] mt-2 text-center">
            {subtitle}
          </p>
        )}

        <p className="text-xs text-[var(--text-muted)] mt-4 text-center italic leading-relaxed">
          {config.hint}
        </p>
      </div>
    </div>
  )
}

// ─── Loader — inline spinner ───────────────────────────────────────────────

export const Loader = () => {
  return (
    <div role="status" className="animate-spin rounded-full h-10 w-10 border-[3px] border-[var(--border-default)] border-t-[var(--brand-primary)]">
      <span className="sr-only">Loading…</span>
    </div>
  )
}
